import React, { useEffect, useRef } from 'react'

import { SEARCH_CHARS_MIN_LIMIT } from '../../consts'
import { getDirectParentFromBreadcrumbs } from '../../utils/breadcrumbs'
import { EmptyState } from './components/EmptyState'
import { Header } from './components/Header'
import { Option } from './components/Option'
import { OptionsSkeleton } from './components/OptionsSkeleton'
import * as S from './styled'
import type { OptionsProps } from './types'

export const Options = ({
  initialCategory,
  selectedCategory,
  searchValue,
  options,
  loading,
  focusState,
  onSelect,
  onNavigateUp,
  onNavigateDown,
}: OptionsProps) => {
  const directParent =
    selectedCategory && getDirectParentFromBreadcrumbs(selectedCategory.breadcrumbs)
  const hasChildren = selectedCategory?.children ? selectedCategory.children.length > 0 : false
  const displayHeader = !searchValue && selectedCategory && (hasChildren || directParent)
  const optionsListRef = useRef<HTMLDivElement>(null)
  const optionRefs = useRef<(HTMLDivElement | null)[]>([])

  useEffect(() => {
    if (focusState.index !== null && optionRefs.current[focusState.index]) {
      optionRefs.current[focusState.index]?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      })
    }
  }, [focusState.index])

  const renderContent = () => {
    if (loading) {
      return <OptionsSkeleton />
    }

    if (searchValue && options.length === 0) {
      return <EmptyState searchValue={searchValue} />
    }

    return (
      <>
        {displayHeader && (
          <S.HeaderContainer>
            <Header
              selectedCategory={selectedCategory}
              focused={focusState.type === 'header'}
              onClick={onNavigateUp}
              data-header
            />
          </S.HeaderContainer>
        )}

        <S.OptionsList ref={optionsListRef}>
          {options.map((option, index) => (
            <S.OptionWrapper key={option.id} ref={(el) => (optionRefs.current[index] = el)}>
              <Option
                searchValue={searchValue.length >= SEARCH_CHARS_MIN_LIMIT ? searchValue : ''}
                selected={initialCategory?.id === option.id}
                isFocused={focusState.type === 'row' && focusState.index === index}
                option={option}
                onSelect={onSelect}
                onNavigateDown={onNavigateDown}
              />
            </S.OptionWrapper>
          ))}
        </S.OptionsList>
      </>
    )
  }

  return <S.Container>{renderContent()}</S.Container>
}
